/**
 * The one label map (ARCHITECTURE-SPINE.md § AD-14 Consistency Conventions:
 * "One label map turns `notify_by_date` into 'Notify-by date' for display,
 * and that map is the only place display names exist"). `DataTable` looks
 * up every column header here by the column's `key` (a real generated-type
 * field name, VC-2.2-03) — no column config carries its own `label` string,
 * so there is nowhere else a header string could be duplicated or
 * hard-coded (VC-2.2-04).
 *
 * Keyed by the API's own `snake_case` field name, not by resource — a field
 * name means the same thing everywhere it appears (`created_at` is always
 * "Member since"... except it usually isn't: different resources may want a
 * different label for the same field name, which is exactly why this map is
 * itself scoped per list further down if a future story needs it. For now,
 * Epic 2 has exactly one real list, so one flat map is the honest amount of
 * structure — do not add resource-scoping ahead of a second list needing it.
 */
const FIELD_LABELS: Record<string, string> = {
  display_name: "Name",
  email: "Email",
  role: "Role",
  status: "Status",
  created_at: "Member since",
  // Story 3.8's Alerts screen (`AlertRuleResource`).
  rule_key: "Alert",
  threshold: "Threshold",
  cadence: "How often",
  terminal_condition: "Clears when",
  ceiling_exempt: "Never stops",
  // Story 4.5's Settings → Backup screen (`BackupRunResource`/`RestoreTestRunResource`).
  run_at: "Ran at",
  outcome: "Outcome",
  size_bytes: "Size",
  checksum: "Checksum",
  // Story 5.2's municipality record page (`MunicipalityResource`).
  county: "County",
  rent_control_cap: "Rent control cap",
  co_inspection_rule: "Continued-occupancy / CO inspection",
  tax_portal_url: "Tax portal",
  tax_lookup_format: "Tax lookup key format",
  water_sewer_biller: "Water/sewer biller",
  county_tax_board: "County tax board",
  appeal_deadline: "Tax appeal deadline",
  registration_office: "Landlord registration filing",
  default_notice_period_days: "Default notice period (days)",
  tax_grace_period_days: "Tax grace period (days)",
  received_vs_postmark_rule: "Received vs. postmark rule",
  delinquent_interest_rate: "Delinquent tax interest rate",
  setup_complete: "Setup complete",
  confirmed_on: "Confirmed on",
  // Story 5.2's reference-list admin screen (`ReferenceListItemResource`).
  list_key: "List",
  value: "Value",
  label: "Label",
  sort_order: "Sort order",
  // Story 5.3's properties list and record page (`PropertyListItemResource`/
  // `PropertyResource`). The twelve resolved-municipal-setting keys
  // (`rent_control_cap` through `delinquent_interest_rate`) reuse the exact
  // labels Story 5.2 already registered above for `MunicipalityResource` —
  // one field name, one label, everywhere it appears (this map's own
  // documented convention).
  nickname: "Nickname",
  full_address: "Address",
  municipality_name: "Municipality",
  block_and_lot: "Block & lot",
  property_type: "Property type",
  year_built: "Year built",
  built_before_1978: "Built before 1978",
  square_footage: "Square footage",
  unit_count: "Units",
  purchase_date: "Purchase date",
  purchase_price: "Purchase price",
  current_estimated_value: "Current estimated value",
  mortgage_servicer: "Mortgage servicer",
  loan_number: "Loan number",
  taxes_escrowed_by_lender: "Taxes escrowed by lender",
  insurance_carrier: "Insurance carrier",
  insurance_policy_number: "Insurance policy number",
  insurance_renewal_date: "Insurance renewal date",
  registration_number: "NJ landlord registration number",
  registration_filing_date: "Registration filing date",
  rent_control_flag: "Subject to rent control",
  notes: "Notes",
  photos: "Photos",
  is_archived: "Archived",
  // Story 5.6's disposition record (`PropertyResource`'s five new fields).
  disposition_date: "Sale date",
  disposition_sale_price: "Sale price",
  disposition_buyer: "Buyer",
  disposition_capital_improvements: "Capital improvements to date",
  disposition_closing_documents: "Closing documents",
  // Story 6.1's tenants list and record page (`TenantResource`). `email`,
  // `notes`, `status` and `is_archived` above are reused verbatim — the same
  // field name means the same thing here (this map's own documented
  // convention).
  full_name: "Name",
  mobile_phone: "Mobile phone",
  preferred_contact_method: "Preferred contact method",
  date_of_birth: "Date of birth",
  employer: "Employer",
  emergency_contact_name: "Emergency contact name",
  emergency_contact_phone: "Emergency contact phone",
  additional_occupants: "Additional occupants",
  pets: "Pets",
  vehicles: "Vehicles",
  move_in_date: "Move-in date",
  move_out_date: "Move-out date",
  id_verified: "ID verified",
  id_verified_date: "ID verified on",
  id_verified_by: "ID verified by",
  // Story 6.5's lease record page (`LeaseResource`). `status`, `start_date`
  // and `end_date` reuse tenant-history's own convention above (`status`)
  // and the field's own plain name (this map's documented convention: one
  // field name, one label, everywhere it appears).
  unit_id: "Unit",
  // Story 6.7's leases list (`LeaseListItemResource`) — resolved,
  // human-readable columns, distinct field names from `unit_id`/the
  // property record's own `nickname`, so both share this one map without
  // colliding.
  unit_label: "Unit",
  property_nickname: "Property",
  // Story 15.21's leases list "Tenants" column
  // (`LeaseListItemResource.tenants`) — same plain field name `LeaseResource.
  // tenants`/`TenantRecord.tsx` already use for the identical concept, no
  // collision since neither of those renders this map's own column header.
  tenants: "Tenants",
  renewed_into_lease_id: "Renewed into",
  start_date: "Start date",
  end_date: "End date",
  lease_type: "Lease type",
  auto_renew: "Auto-renews",
  notice_period_days: "Notice period (days)",
  holdover_terms: "Holdover terms",
  holdover_rent_cents: "Holdover rent",
  monthly_rent_cents: "Monthly rent",
  //: Owner-requested (2026-08-28) — the Properties list's own new "Rent"
  //: column (`PropertyListItemResource.in_force_monthly_rent_cents`, summed
  //: across every in-force lease on the property). Deliberately its own,
  //: shorter label rather than reusing `monthly_rent_cents`'s "Monthly
  //: rent" above: that field is one lease's own rent; this one is a
  //: property-wide sum that can span more than one lease, so the identical
  //: label would misrepresent it as the same kind of figure.
  in_force_monthly_rent_cents: "Rent",
  rent_due_day: "Rent due day",
  grace_period_days: "Grace period (days)",
  late_fee_cents: "Late fee",
  returned_check_fee_cents: "Returned check fee",
  pet_rent_cents: "Pet rent",
  parking_fee_cents: "Parking fee",
  storage_fee_cents: "Storage fee",
  utilities_included: "Utilities included",
  scheduled_escalation: "Scheduled rent increases",
  deposit_amount_cents: "Deposit amount",
  deposit_date_received: "Deposit received on",
  deposit_bank_name: "Deposit bank",
  deposit_account_number: "Deposit account number",
  deposit_interest_bearing: "Deposit is interest-bearing",
  deposit_last_interest_paid_date: "Deposit interest last paid on",
  deposit_annual_interest_amount_cents: "Deposit annual interest",
  deposit_disposition: "Deposit disposition",
  deposit_location_notice_date: "Deposit location notice date",
  truth_in_renting_provided_date: "Truth in Renting statement provided on",
  lead_paint_disclosure_provided_date: "Lead paint disclosure provided on",
  registration_statement_copy_provided_date: "Registration statement copy provided on",
  lease_documents: "Lease documents",
  // Story 6.6's notify-by date (`LeaseResource.notify_by`) — the AC's own
  // exact, verbatim label ("Notify-by date" — never "decision deadline",
  // which the glossary retires).
  notify_by: "Notify-by date",
  // Story 7.2's documents list and property record's own inline documents
  // section (`DocumentListItemResource`). `size_bytes` is reused verbatim
  // from Story 4.5's backup screen above (this map's own documented
  // convention: one field name, one label, everywhere it appears).
  // `uploaded_at` is deliberately a distinct field name from `created_at`
  // (already reserved above for `UsersList`'s "Member since") — see
  // `app/api/documents.py::DocumentListItemResource`'s own doc comment.
  original_filename: "Filename",
  content_type: "File type",
  category: "Category",
  entity_label: "Attached to",
  uploaded_at: "Uploaded on",
  // Story 7.3: settable at upload time and via a real edit
  // (`AddDocumentDialog.tsx`, `DocumentsList.tsx`'s own inline-edit column).
  expiration_date: "Expires on",
  // Story 8.2's import wizard preview table (`LeaseImportRowResource`).
  // `property_nickname`/`unit_label`/`start_date`/`status`/`end_date`/
  // `monthly_rent_cents`/`holdover_rent_cents` all reuse the exact labels
  // already registered above (Story 6.7's leases list, Story 6.3's lease
  // record page) — one field name, one label, everywhere it appears (this
  // map's own documented convention). `row_number`/`import_error` are the
  // two genuinely new fields this preview row shape adds beyond the
  // `Lease` fields it previews.
  row_number: "Row",
  import_error: "Result",
  // Story 8.3's generalized multi-entity import wizard — the field names
  // its own new file types (`municipalities.csv`/`units.csv`/
  // `lease_tenants.csv`) add beyond what Story 8.2 already registered above.
  // `full_name`/`mobile_phone`/`email`/`nickname`/`property_nickname`/
  // `unit_label`/`start_date`/`notice_period_days` all reuse the exact
  // labels already registered above (this map's own documented
  // convention).
  name: "Name",
  municipality_county: "Municipality county",
  intent: "Intent",
  tenant_full_name: "Tenant name",
  tenant_mobile_phone: "Tenant mobile phone",
  // Story 8.4's own import batches list (`ImportBatchListItemResource`).
  // `label`/`run_at` reuse the exact labels already registered above
  // (`ReferenceListItemResource`'s own `label`, Story 4.5's `BackupRunResource.
  // run_at` — "Ran at" fits an import run exactly as well as a backup run).
  row_count: "Records",
  already_rolled_back: "Status",
  // Gap-fill (frontend auth UI): the sign-in/accept-invitation/reset-password
  // screens' one password field (`SignInRequest.password`, `AcceptInvitation
  // Request.password`, `ResetPasswordRequest.password` — one field name,
  // one label, reused across all three forms this map's own documented
  // convention). `email` above is already reused verbatim from Story 6.1's
  // tenant form for the sign-in screen's email field.
  password: "Password",
  // Story 11.1's own renewal record (`RenewalResource`). `status`/
  // `notice_period_days` reuse the exact labels already registered above.
  lease_id: "Lease",
  term: "Intended term start",
  owner_intent: "Owner intent",
  non_renewal_cause: "Non-renewal cause",
  notice_type: "Notice type",
  intended_termination_date: "Intended termination date",
  proposed_rent_cents: "Proposed rent",
  current_rent_cents: "Current rent",
  market_note: "Market note",
  date_owner_notified_tenant: "Date tenant notified",
  tenant_response: "Tenant response",
  tenant_response_date: "Tenant response date",
  next_follow_up_date: "Next follow-up",
  new_lease_id: "New lease",
  // Story 11.5 (R-N10, R-N11): the statutory-notice pair PRD §4.5 names but
  // Story 11.1 never built, plus the computed serve-by date/provenance.
  notice_served_on: "Notice served on",
  notice_served_method: "Notice served — method",
  serve_by: "Notice serve-by date",
  // Story 12.1's own equipment record (`EquipmentResource`). `purchase_date`/
  // `status`/`unit_id`/`property_nickname`/`unit_label`/`category` reuse
  // the exact labels already registered above.
  prop_id: "Property",
  make: "Make",
  model_number: "Model number",
  serial_number: "Serial number",
  capacity: "Capacity",
  fuel_type: "Fuel",
  specific_location: "Specific location",
  purchase_price_cents: "Purchase price",
  retailer_or_installer: "Retailer or installer",
  warranty_type: "Warranty type",
  warranty_length: "Warranty length",
  warranty_expiration_date: "Warranty expiration date",
  has_extended_warranty: "Extended warranty",
  claim_phone_number: "Warranty claim phone number",
  expected_useful_life_years: "Expected useful life (years)",
  replacement_cost_estimate_cents: "Replacement cost estimate",
  condition: "Condition",
  service_interval: "Service interval",
  service_interval_months: "Service interval (months)",
  last_service_date: "Last service date",
  // Story 12.5 — the replace-and-retire link.
  replaced_by_equipment_id: "Replaced by",
  // Story 15.2 — the document replace-and-supersede link. Same label text
  // as `replaced_by_equipment_id` above (one concept, one wording), a
  // distinct field name only because it lives on a different entity.
  replaced_by_document_id: "Replaced by",
  // Story 15.12 — the Tenants list's own Lease start/end columns
  // (`TenantResource.current_lease_start_date`/`current_lease_end_date`),
  // replacing the removed "Move-in date" column. Distinct field names from
  // `start_date`/`end_date` above (those belong to `LeaseResource` itself),
  // so both share this one map without colliding — text matches
  // `TenantRecord.tsx`'s own key-facts strip labels verbatim (spec
  // judgement call 3: one concept, one wording, across both screens).
  current_lease_start_date: "Lease start",
  current_lease_end_date: "Lease end",
  // Story 15.13 — the Properties list's own new Lease start/end columns
  // (`PropertyListItemResource.soonest_lease_start_date`/`soonest_lease_
  // end_date`), the property-scoped analogue of Story 15.12's Tenant-list
  // pair immediately above. Distinct field names (matching `PropertyKeyFacts
  // Resource`'s own nested field names, `app/api/properties.py`) — the
  // property side's own concept is "soonest-upcoming-to-end", never
  // "current", unlike a tenant's own current lease — but the same "Lease
  // start"/"Lease end" label text, since both columns describe the same
  // idea in the same table shape (`PropertyRecord.tsx`'s own key-facts strip
  // uses this identical text for the identical concept).
  soonest_lease_start_date: "Lease start",
  soonest_lease_end_date: "Lease end",
  // Story 16.2's vendor directory (`VendorResource`). `full_name`/
  // `mobile_phone`/`email`/`preferred_contact_method`/`notes` all reuse the
  // exact labels already registered above (Story 6.1's tenant fields) — one
  // field name, one label, everywhere it appears (this map's own documented
  // convention): both `Tenant` and `Vendor` share the same `Contact` base.
  // `trade`/`company_name` are `Vendor`'s own two genuinely new fields.
  trade: "Trade",
  company_name: "Company",
  // Story 15.26 — the Tenants list's own new "Property" column
  // (`TenantResource.current_property_nickname`), the reverse direction of
  // `property_nickname` above (the Leases list's own identical label for
  // the identical concept, one field name apart only because it lives on a
  // different resource): the property behind a tenant's current lease.
  current_property_nickname: "Property",
};

/**
 * Throws rather than silently falling back to a humanised guess: a missing
 * label is a configuration mistake this story would rather fail loudly on
 * (the same "fail loud, not a wrong default" instinct the backend's own
 * domain errors follow) than render `created_at` as a raw field name to a
 * family member who has never seen the word "snake_case".
 */
export function labelFor(field: string): string {
  const label = FIELD_LABELS[field];
  if (label === undefined) {
    throw new Error(
      `No display label is registered for field "${field}" in lib/field-labels.ts — add one there, the one place labels live.`,
    );
  }
  return label;
}
